import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useLanguage } from "../../hooks/useLanguage";
import { format } from "date-fns";
import {
  addTrainingDoc, getTrainingDocs, updateTrainingDoc, deleteTrainingDoc,
} from "../../firebase/services";

const EMPTY = { date: format(new Date(), "yyyy-MM-dd"), title: "", content: "", linkUrl: "" };

export default function TrainingPage() {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const canManage = profile?.role === "admin" || profile?.role === "supervisor";

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getTrainingDocs().then(d => { setDocs(d); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setMsg(t("train.need_title")); return; }
    setSaving(true);
    try {
      if (editId) {
        await updateTrainingDoc(editId, { date: form.date, title: form.title.trim(), content: form.content.trim(), linkUrl: form.linkUrl.trim() });
      } else {
        await addTrainingDoc({
          date: form.date, title: form.title.trim(), content: form.content.trim(), linkUrl: form.linkUrl.trim(),
          createdByName: profile?.name || "", createdByRole: profile?.role || "",
        });
      }
      setForm(EMPTY); setEditId(null); setMsg(t("train.saved"));
      load();
    } catch (err) { setMsg("Lỗi: " + err.message); }
    setSaving(false);
    setTimeout(() => setMsg(""), 3000);
  };

  const startEdit = (d) => {
    setEditId(d.id);
    setForm({ date: d.date || format(new Date(), "yyyy-MM-dd"), title: d.title || "", content: d.content || "", linkUrl: d.linkUrl || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditId(null); setForm(EMPTY); };
  const handleDelete = async (d) => {
    if (!window.confirm(t("train.del_confirm"))) return;
    await deleteTrainingDoc(d.id);
    load();
  };

  const validLink = (url) => /^https?:\/\//i.test(url || "");

  return (
    <>
      <div className="page-header">
        <div><h1>📚 {t("nav.training")}</h1><p>{t("train.subtitle")}</p></div>
      </div>

      {msg && <div className={`alert ${msg.startsWith("✅") ? "alert-success" : "alert-error"}`} style={{ marginBottom: 14 }}>{msg}</div>}

      {canManage && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 10 }}>{editId ? t("train.edit_title") : t("train.add_title")}</div>
          <form onSubmit={handleSave}>
            <div className="grid-2" style={{ gap: 10 }}>
              <div className="form-group">
                <label className="form-label">{t("train.date")}</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("train.title_lb")}</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="VD: Hướng dẫn tư vấn dòng Titan" required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t("train.content")}</label>
              <textarea rows={3} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Mô tả ngắn về tài liệu..." />
            </div>
            <div className="form-group">
              <label className="form-label">{t("train.link")}</label>
              <input value={form.linkUrl} onChange={e => setForm(f => ({ ...f, linkUrl: e.target.value }))} placeholder="https://drive.google.com/..." style={{ fontFamily: "monospace", fontSize: 13 }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? t("common.saving") : (editId ? t("train.save_change") : t("train.add_btn"))}</button>
              {editId && <button type="button" className="btn" onClick={cancelEdit}>{t("common.cancel")}</button>}
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-title" style={{ marginBottom: 10 }}>{t("train.list")} ({docs.length})</div>
        {loading ? (
          <div style={{ color: "#888", padding: 16, textAlign: "center" }}>{t("common.loading")}</div>
        ) : docs.length === 0 ? (
          <div style={{ color: "#aaa", padding: 24, textAlign: "center" }}>{t("train.none")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {docs.map(d => (
              <div key={d.id} style={{ border: "1px solid #eee", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 11, color: "#888" }}>{d.date}{d.createdByName ? ` · ${d.createdByName}` : ""}</div>
                    <div style={{ fontWeight: 600, marginTop: 2 }}>{d.title}</div>
                    {d.content && <div style={{ fontSize: 13, color: "#555", marginTop: 4, whiteSpace: "pre-wrap" }}>{d.content}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {validLink(d.linkUrl) && (
                      <a className="btn btn-primary btn-sm" href={d.linkUrl} target="_blank" rel="noopener noreferrer">{t("train.open")}</a>
                    )}
                    {canManage && (
                      <>
                        <button className="btn btn-sm" onClick={() => startEdit(d)}>✎</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(d)}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
